/*
 * Copyright (C) 2022 to Present Applause App Quality, Inc. All rights reserved.
 */

import { ElementRef, EmbeddedViewRef, Injectable, NgZone, OnDestroy, QueryList, ViewContainerRef } from '@angular/core';
import { combineLatest, Observable, Subject } from 'rxjs';
import { distinctUntilChanged, map, switchMap, tap } from 'rxjs/operators';
import { queryListChanges } from '../../+utils/query-list-changes';
import { ResponsivePillListBreakpoint } from './responsive-pill-list-breakpoint';
import { ResponsivePillListResizeService } from './responsive-pill-list-resize.service';
import { ResponsivePillContext, ResponsivePillDirective } from './responsive-pill.directive';
import { ShadowResponsivePillListComponent } from './shadow-responsive-pill-list/shadow-responsive-pill-list.component';


@Injectable()
export class ResponsivePillListService implements OnDestroy {

  private readonly RESERVED_TOGGLE_SPACE_PX = 50;

  currentBreakpoint$: Observable<ResponsivePillListBreakpoint>;

  set minPills(value: number) {
    this.currentBreakpoint = null;
    this._minPills.next(value);
  }

  set maxPills(value: number) {
    this.currentBreakpoint = null;
    this._maxPills.next(value);
  }

  private hostViewContainer: ViewContainerRef;
  private hostElement: ElementRef;
  private currentBreakpoint: ResponsivePillListBreakpoint | null = null;

  private readonly responsivePills = new Subject<QueryList<ResponsivePillDirective>>();
  private readonly _minPills = new Subject<number>();
  private readonly _maxPills = new Subject<number>();
  private readonly resize = new Subject<DOMRectReadOnly>();

  constructor(private zone: NgZone,
              private resizeService: ResponsivePillListResizeService) {
    const breakpoints$ = combineLatest(
      [
        this.responsivePills,
        this.resize,
      ]).pipe(
        map(([responsivePills, _]) => responsivePills),
        switchMap((queryList) => queryListChanges(queryList)),
        switchMap((responsivePills) => this.buildShadowEmbeddedViews(responsivePills)),
        map((embeddedViews) => this.calculateBreakpoints(embeddedViews)),
      );

    this.currentBreakpoint$ = combineLatest([
      breakpoints$,
      this.resize,
      this._minPills,
      this._maxPills,
    ]).pipe(
      map(([breakpoints, hostRect, minPills, maxPills]) => this.calculateCurrentBreakpoint(breakpoints, hostRect, minPills, maxPills)),
      distinctUntilChanged(),
      tap((breakpoint) => this.currentBreakpoint = breakpoint),
    );
  }

  ngOnDestroy() {
    if (this.hostElement) {
      this.resizeService.unobserve(this.hostElement.nativeElement);
    }
  }

  register(hostViewContainer: ViewContainerRef,
           hostElement: ElementRef,
           responsivePills: QueryList<ResponsivePillDirective>,
           minPills: number,
           maxPills: number): void {
    this.hostViewContainer = hostViewContainer;
    this.hostElement = hostElement;
    this.responsivePills.next(responsivePills);
    this.minPills = minPills;
    this.maxPills = maxPills;

    // need to run callback inside zone so change detection works correctly ¯\_(ツ)_/¯
    this.resizeService.observe(
      hostElement.nativeElement,
      (entry) => this.zone.run(() => this.resize.next(entry.contentRect))
    );
  }

  private buildShadowEmbeddedViews(responsivePills: ResponsivePillDirective[]): Observable<EmbeddedViewRef<ResponsivePillContext>[]> {
    const shadowRef = this.hostViewContainer.createComponent(ShadowResponsivePillListComponent);
    shadowRef.instance.responsivePills = responsivePills;
    shadowRef.changeDetectorRef.detach(); // we only need to detect changes once
    shadowRef.changeDetectorRef.detectChanges();
    return shadowRef.instance.embeddedViews$;
  }

  private calculateBreakpoints(embeddedViews: EmbeddedViewRef<ResponsivePillContext>[]): ResponsivePillListBreakpoint[] {
    const nativeElement: HTMLElement = this.hostElement.nativeElement;
    const hostLeft = nativeElement.getBoundingClientRect().left;

    const breakpoints: ResponsivePillListBreakpoint[] = [];
    for (let index = 0, start = -Infinity; index <= embeddedViews.length; index++) {
      const current = embeddedViews[index];
      const last = index === embeddedViews.length - 1;
      const end = current
        ? this.getAdjustedEnd(current, hostLeft, last)
        : Infinity;
      breakpoints.push({
        index,
        start,
        end
      });
      start = end;
    }

    this.currentBreakpoint = null;
    this.hostViewContainer.clear(); // remove shadow component from view
    return breakpoints;
  }

  private getAdjustedEnd(embeddedView: EmbeddedViewRef<ResponsivePillContext>, hostLeft: number, last: boolean) {
    const adjustment = last ? 0 : this.RESERVED_TOGGLE_SPACE_PX;
    // multiple rootNodes possible if template is defined as ng-container, so we look for rightmost
    const rights = embeddedView.rootNodes
      .filter((node: any) => node instanceof HTMLElement && typeof node.getBoundingClientRect === 'function')
      .map((node: HTMLElement) => node.getBoundingClientRect().right);
    const maxRight = Math.max(...rights);

    return Math.ceil(maxRight - hostLeft + adjustment);
  }

  /*
    Determines how many templates can be rendered without overflowing host element.
   */
  private calculateCurrentBreakpoint(breakpoints: ResponsivePillListBreakpoint[],
                                     hostRect: DOMRectReadOnly,
                                     minPills: number,
                                     maxPills: number): ResponsivePillListBreakpoint {
    const hostWidth = hostRect.width;
    const currentBreakpoint = this.currentBreakpoint;
    if (currentBreakpoint
      && currentBreakpoint.start <= hostWidth
      && hostWidth < currentBreakpoint.end) {
      // no need to compute as we are in same range, short circuit
      return currentBreakpoint;
    }

    // finding a candidate is guaranteed as they span from -Infinity to Infinity
    const candidate = breakpoints.find(({start, end}) => start <= hostWidth && hostWidth < end);
    return this.getBreakpoint(candidate, breakpoints, minPills, maxPills);
  }

  private getBreakpoint(candidate: ResponsivePillListBreakpoint,
                        breakpoints: ResponsivePillListBreakpoint[],
                        minPills: number,
                        maxPills: number) {
    const effectiveMinPills = Math.min(minPills, breakpoints.length - 1);
    if (candidate.index < effectiveMinPills) {
      return breakpoints[effectiveMinPills];
    } else if (maxPills < candidate.index) {
      return breakpoints[maxPills];
    }

    return candidate;
  }
}
